// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { showNotification } from '@mantine/notifications';
import { getReferenceString } from '@medplum/core';
import { ErrorBoundary, Loading, useMedplum, useMedplumProfile } from '@medplum/react';
import { NotificationBell } from '@polarissleep/messaging';
import { NotificationAudience } from '@polarissleep/shared';
import {
  IconBell,
  IconCalendarDue,
  IconClipboardList,
  IconDeviceHeartMonitor,
  IconFilePlus,
  IconReportMoney,
  IconSettings,
  IconUser,
  IconUsersGroup,
} from '@tabler/icons-react';
import type { ElementType, JSX } from 'react';
import { Fragment, Suspense, useEffect } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate } from 'react-router';
import { AppShell } from './components/layout/AppShell';
import { PageContainer } from './components/layout/PageContainer';
import { RequirePermission } from './components/RequirePermission';
import { SidebarNotificationIcon } from './components/SidebarNotificationIcon';
import { SidebarUnreadInboxIcon } from './components/SidebarUnreadInboxIcon';
import { TaskDetailsModal } from './components/tasks/TaskDetailsModal';
import { useProfileCompletion } from './hooks/useProfileCompletion';
import { useRole } from './hooks/useRole';
import { hasPermission } from './utils/permissions';
import './index.css';
import { useProviderNotifications } from './notifications/useProviderNotifications';
import { NotificationEditorByKey } from './pages/admin/NotificationEditorByKey';
import { NotificationPreferences } from './pages/admin/NotificationPreferences';
import { NotificationTemplateEditor } from './pages/admin/NotificationTemplateEditor';
import { NotificationTemplateList } from './pages/admin/NotificationTemplateList';
import { UserOrgScope } from './pages/admin/UserOrgScope';
import { ZoomIntegrationTest } from './pages/admin/ZoomIntegrationTest';
import { AppointmentSessionPage } from './pages/appointment/AppointmentSessionPage';
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage';
import { InviteExpiredPage } from './pages/auth/InviteExpiredPage';
import { SetPasswordPage } from './pages/auth/SetPasswordPage';
import { BillingShell } from './pages/billing/BillingShell';
import { ChargemasterTab } from './pages/billing/ChargemasterTab';
import { ClaimDetailPage } from './pages/billing/ClaimDetailPage';
import { ClaimsTab } from './pages/billing/ClaimsTab';
import { EligibilityTab } from './pages/billing/EligibilityTab';
import { PatientPaymentsTab } from './pages/billing/PatientPaymentsTab';
import { DashboardPage } from './pages/DashboardPage';
import { EncounterChartPage } from './pages/encounter/EncounterChartPage';
import { EncounterPatientWorkspacePage } from './pages/encounter/EncounterPatientWorkspacePage';
import { NpIntakeFormPage } from './pages/encounter/NpIntakeFormPage';
import { LandingPage } from './pages/LandingPage';
import { MessagesPage } from './pages/messages/MessagesPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { OrdersPage } from './pages/orders/OrdersPage';
import { AppointmentsTab } from './pages/patient/appointments/AppointmentsTab';
import { ReschedulePage } from './pages/patient/appointments/ReschedulePage';
import { SchedulingPage } from './pages/patient/appointments/SchedulingPage';
import { CommunicationTab } from './pages/patient/CommunicationTab';
import { CommunicationLogTab } from './pages/patient/comms-log/CommunicationLogTab';
import { DmeOrderPage } from './pages/patient/DmeOrderPage';
import { DevicesTab } from './pages/patient/devices/DevicesTab';
import { PatientListEmptyState } from './pages/patient/list/PatientListEmptyState';
import { PatientListLayout } from './pages/patient/list/PatientListLayout';
import { DiagnosticReportsPage } from './pages/patient/medical-info/DiagnosticReportsPage';
import { IntakeScreenerPage } from './pages/patient/medical-info/IntakeScreenerPage';
import { MedicalInfoTab } from './pages/patient/medical-info/MedicalInfoTab';
import { OrderPatientHSATPage } from './pages/patient/OrderPatientHSATPage';
import { OrdersTab } from './pages/patient/OrdersTab';
import { PatientPage } from './pages/patient/PatientPage';
import { PatientSearchPage } from './pages/patient/PatientSearchPage';
import { ProfileTab } from './pages/patient/profile/ProfileTab';
import { RpmOrderPage } from './pages/patient/RpmOrderPage';
import { TasksTab } from './pages/patient/TasksTab';
import { PCPReferralPage } from './pages/pcp-referral/PCPReferralPage';
import { ScriptSurePrescribingPage } from './pages/prescribing/ScriptSurePrescribingPage';
import { ResourceCreatePage } from './pages/resource/ResourceCreatePage';
import { ResourceDetailPage } from './pages/resource/ResourceDetailPage';
import { ResourceEditPage } from './pages/resource/ResourceEditPage';
import { ResourceHistoryPage } from './pages/resource/ResourceHistoryPage';
import { ResourcePage } from './pages/resource/ResourcePage';
import { SearchPage } from './pages/SearchPage';
import { SignInPage } from './pages/SignInPage';
import { SchedulePage } from './pages/schedule/SchedulePage';
import { PractitionerSettingsPage } from './pages/settings/PractitionerSettingsPage';
import { ProviderRolePage } from './pages/settings/ProviderRolePage';
import { StaffDetailPage } from './pages/staffing/StaffDetailPage';
import { StaffingPage } from './pages/staffing/StaffingPage';
import { TasksPage } from './pages/tasks/TasksPage';
import { InviteProviderPage } from './pages/team/InviteProviderPage';
import { CompositionReviewPage } from './pages/triage/CompositionReviewPage';
import { IntakeReviewPage } from './pages/triage/IntakeReviewPage';
import { MissingQuestionnairesPage } from './pages/triage/MissingQuestionnairesPage';
import { TriagePage } from './pages/triage/TriagePage';

export function App(): JSX.Element | null {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const location = useLocation();
  const navigate = useNavigate();
  const notificationsResult = useProviderNotifications();
  const { role, isAdmin, loading: roleLoading } = useRole();
  const {
    isRequired: profileRequired,
    isComplete: profileComplete,
    loading: profileCompletionLoading,
  } = useProfileCompletion();
  const needsOnboarding = !!profile && profileRequired && !profileCompletionLoading && !profileComplete;
  const wrongProfileType = !!profile && profile.resourceType !== 'Practitioner';
  const skipPageContainer =
    !profile ||
    location.pathname === '/Patient' ||
    location.pathname.startsWith('/Patient/') ||
    location.pathname === '/Communication' ||
    location.pathname.startsWith('/Communication/') ||
    location.pathname === '/Task' ||
    location.pathname.startsWith('/Task/');
  const RoutesWrapper: ElementType = skipPageContainer ? Fragment : PageContainer;
  const wrapperProps = skipPageContainer ? {} : { fullHeight: true };

  useEffect(() => {
    if (!wrongProfileType) {
      return;
    }
    showNotification({
      color: 'red',
      title: 'Access denied',
      message: 'This account is not a provider account. Please use the patient app.',
    });
    medplum
      .signOut()
      .catch(console.error)
      .finally(() => {
        window.location.href = '/';
      });
  }, [wrongProfileType, medplum]);

  if (location.pathname.startsWith('/pcp-referral')) {
    return (
      <ErrorBoundary>
        <Suspense fallback={<Loading />}>
          <Routes>
            <Route path="/pcp-referral" element={<PCPReferralPage />} />
            <Route path="/pcp-referral/:referralId" element={<PCPReferralPage />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    );
  }

  if (medplum.isLoading()) {
    return null;
  }

  if (wrongProfileType && location.pathname !== '/signin') {
    return <Navigate to="/signin" replace />;
  }

  return (
    <AppShell
      notifications={
        profile ? (
          <NotificationBell
            notificationsResult={notificationsResult}
            audience={NotificationAudience.PROVIDER}
            viewAllHref="/notifications"
            navigate={navigate}
          />
        ) : undefined
      }
      menus={
        profile
          ? [
              {
                links: [
                  ...(hasPermission({
                    role,
                    permission: 'chat-with-patient',
                    isAdmin,
                  })
                    ? [
                        {
                          icon: <SidebarUnreadInboxIcon recipientRef={getReferenceString(profile)} />,
                          label: 'Inbox',
                          href: `/Communication?bucket=unread`,
                        },
                      ]
                    : []),
                  ...(hasPermission({
                    role,
                    permission: 'view-patient-list',
                    isAdmin,
                  })
                    ? [
                        {
                          icon: <IconUser />,
                          label: 'Patients',
                          href: '/Patient',
                        },
                      ]
                    : []),
                  ...(hasPermission({
                    role,
                    permission: 'review-triage',
                    isAdmin,
                  })
                    ? [
                        {
                          icon: <IconClipboardList />,
                          label: 'Triage Queue',
                          href: '/triage',
                        },
                      ]
                    : []),
                  ...(hasPermission({
                    role,
                    permission: 'schedule-appointment',
                    isAdmin,
                  })
                    ? [
                        {
                          icon: <IconCalendarDue />,
                          label: 'Appointments',
                          href: '/schedule',
                        },
                      ]
                    : []),
                  ...(hasPermission({
                    role,
                    permission: 'view-tasks',
                    isAdmin,
                  })
                    ? [
                        {
                          icon: (
                            <SidebarNotificationIcon
                              resourceType="Task"
                              countCriteria={`owner=${getReferenceString(profile)}&status=requested,ready,received,accepted,in-progress,draft&_summary=count`}
                              subscriptionCriteria={`Task?owner=${getReferenceString(profile)}`}
                              iconComponent={<IconFilePlus />}
                            />
                          ),
                          label: 'Tasks',
                          href: `/Task?owner=${getReferenceString(profile)}&_sort=-_lastUpdated&status=requested,ready,received,accepted,in-progress,draft`,
                        },
                      ]
                    : []),
                  ...(hasPermission({
                    role,
                    permission: 'view-staffing',
                    isAdmin,
                  })
                    ? [
                        {
                          icon: <IconUsersGroup />,
                          label: 'Staffing',
                          href: '/admin/staffing',
                        },
                      ]
                    : []),
                  ...(hasPermission({
                    role,
                    permission: 'manage-notification-templates',
                    isAdmin,
                  })
                    ? [
                        {
                          icon: <IconBell />,
                          label: 'Notification Templates',
                          href: '/admin/notification-templates',
                        },
                      ]
                    : []),
                  ...(hasPermission({
                    role,
                    permission: 'admin-panel',
                    isAdmin,
                  })
                    ? [
                        {
                          icon: <IconSettings />,
                          label: 'Notification Preferences',
                          href: '/admin/notification-preferences',
                        },
                      ]
                    : []),
                  ...(hasPermission({
                    role,
                    permission: 'place-hsat-order',
                    isAdmin,
                  })
                    ? [
                        {
                          icon: <IconDeviceHeartMonitor />,
                          label: 'Orders',
                          href: '/orders?view=hsat',
                        },
                      ]
                    : []),
                  ...(hasPermission({
                    role,
                    permission: 'manage-billing',
                    isAdmin,
                  })
                    ? [
                        {
                          icon: <IconReportMoney />,
                          label: 'Billing',
                          href: '/billing',
                        },
                      ]
                    : []),
                  {
                    icon: <IconSettings />,
                    label: 'Account',
                    href: '/settings',
                  },
                ],
              },
            ]
          : undefined
      }
    >
      <Suspense fallback={<Loading />}>
        <RoutesWrapper {...wrapperProps}>
          <Routes>
            {profile && needsOnboarding && (
              <>
                <Route path="/settings" element={<PractitionerSettingsPage />} />
                <Route path="/signin" element={<SignInPage />} />
                <Route path="*" element={<Navigate to="/settings" replace />} />
              </>
            )}
            {profile && !needsOnboarding && (
              <>
                <Route
                  path="/"
                  element={role === 'clinical-ops-rcm' ? <Navigate to="/billing/claims" replace /> : <DashboardPage />}
                />
                <Route element={<PatientListLayout />}>
                  <Route
                    path="/Patient"
                    element={
                      <RequirePermission
                        role={role}
                        isAdmin={isAdmin}
                        loading={roleLoading}
                        permission="view-patient-list"
                        redirectTo="/billing/claims"
                      >
                        <Outlet />
                      </RequirePermission>
                    }
                  >
                    <Route index element={<PatientListEmptyState />} />
                    <Route path="new" element={<ResourceCreatePage />} />
                  </Route>
                  <Route path="/Patient/:patientId" element={<PatientPage />}>
                    <Route
                      path="Encounter/:encounterId"
                      element={
                        <RequirePermission
                          role={role}
                          isAdmin={isAdmin}
                          loading={roleLoading}
                          permission="view-clinical-chart"
                          redirectTo="/billing/claims"
                        >
                          <EncounterChartPage />
                        </RequirePermission>
                      }
                    >
                      <Route path="Task/:taskId" element={<TaskDetailsModal />} />
                    </Route>
                    <Route
                      path="Encounter/:encounterId/profile"
                      element={
                        <RequirePermission
                          role={role}
                          isAdmin={isAdmin}
                          loading={roleLoading}
                          permission="view-clinical-chart"
                          redirectTo="/billing/claims"
                        >
                          <EncounterPatientWorkspacePage currentTab="profile" />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="Encounter/:encounterId/medical-info"
                      element={
                        <RequirePermission
                          role={role}
                          isAdmin={isAdmin}
                          loading={roleLoading}
                          permission="view-clinical-chart"
                          redirectTo="/billing/claims"
                        >
                          <EncounterPatientWorkspacePage currentTab="medical-info" />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="Encounter/:encounterId/coordinator-note"
                      element={
                        <RequirePermission
                          role={role}
                          isAdmin={isAdmin}
                          loading={roleLoading}
                          permission="view-clinical-chart"
                          redirectTo="/billing/claims"
                        >
                          <EncounterPatientWorkspacePage currentTab="coordinator-note" />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="Encounter/:encounterId/intake-form"
                      element={
                        <RequirePermission
                          role={role}
                          isAdmin={isAdmin}
                          loading={roleLoading}
                          permission="complete-intake-form"
                        >
                          <NpIntakeFormPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="Encounter/:encounterId/intake-screener"
                      element={
                        <RequirePermission
                          role={role}
                          isAdmin={isAdmin}
                          loading={roleLoading}
                          permission="view-clinical-chart"
                          redirectTo="/billing/claims"
                        >
                          <IntakeScreenerPage />
                        </RequirePermission>
                      }
                    />
                    <Route path="Appointment" element={<AppointmentsTab />} />
                    <Route
                      path="Appointment/schedule"
                      element={
                        <RequirePermission
                          role={role}
                          isAdmin={isAdmin}
                          loading={roleLoading}
                          permission="schedule-appointment"
                        >
                          <SchedulingPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="Appointment/reschedule/:appointmentId"
                      element={
                        <RequirePermission
                          role={role}
                          isAdmin={isAdmin}
                          loading={roleLoading}
                          permission="schedule-appointment"
                        >
                          <ReschedulePage />
                        </RequirePermission>
                      }
                    />
                    <Route path="profile" element={<ProfileTab />} />
                    <Route
                      path="medical-info"
                      element={
                        <RequirePermission
                          role={role}
                          isAdmin={isAdmin}
                          loading={roleLoading}
                          permission="view-clinical-chart"
                          redirectTo="/billing/claims"
                        >
                          <MedicalInfoTab />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="intake-screener"
                      element={
                        <RequirePermission
                          role={role}
                          isAdmin={isAdmin}
                          loading={roleLoading}
                          permission="view-clinical-chart"
                          redirectTo="/billing/claims"
                        >
                          <IntakeScreenerPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="diagnostic-reports"
                      element={
                        <RequirePermission
                          role={role}
                          isAdmin={isAdmin}
                          loading={roleLoading}
                          permission="view-clinical-chart"
                          redirectTo="/billing/claims"
                        >
                          <DiagnosticReportsPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="devices"
                      element={
                        <RequirePermission
                          role={role}
                          isAdmin={isAdmin}
                          loading={roleLoading}
                          permission="view-clinical-chart"
                          redirectTo="/billing/claims"
                        >
                          <DevicesTab />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="orders"
                      element={
                        <RequirePermission
                          role={role}
                          isAdmin={isAdmin}
                          loading={roleLoading}
                          permission="view-clinical-chart"
                          redirectTo="/billing/claims"
                        >
                          <OrdersTab />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="comms-log"
                      element={
                        <RequirePermission
                          role={role}
                          isAdmin={isAdmin}
                          loading={roleLoading}
                          permission="view-clinical-chart"
                          redirectTo="/billing/claims"
                        >
                          <CommunicationLogTab />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="Communication"
                      element={
                        <RequirePermission
                          role={role}
                          isAdmin={isAdmin}
                          loading={roleLoading}
                          permission="view-clinical-chart"
                          redirectTo="/billing/claims"
                        >
                          <CommunicationTab />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="Communication/:messageId"
                      element={
                        <RequirePermission
                          role={role}
                          isAdmin={isAdmin}
                          loading={roleLoading}
                          permission="view-clinical-chart"
                          redirectTo="/billing/claims"
                        >
                          <CommunicationTab />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="Task"
                      element={
                        <RequirePermission
                          role={role}
                          isAdmin={isAdmin}
                          loading={roleLoading}
                          permission="view-clinical-chart"
                          redirectTo="/billing/claims"
                        >
                          <TasksTab />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="Task/:taskId"
                      element={
                        <RequirePermission
                          role={role}
                          isAdmin={isAdmin}
                          loading={roleLoading}
                          permission="view-clinical-chart"
                          redirectTo="/billing/claims"
                        >
                          <TasksTab />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="prescribe"
                      element={
                        <RequirePermission
                          role={role}
                          isAdmin={isAdmin}
                          loading={roleLoading}
                          permission="prescribe-medications"
                          redirectTo="/billing/claims"
                        >
                          <ScriptSurePrescribingPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path=":resourceType"
                      element={
                        <RequirePermission
                          role={role}
                          isAdmin={isAdmin}
                          loading={roleLoading}
                          permission="view-clinical-chart"
                          redirectTo="/billing/claims"
                        >
                          <PatientSearchPage />
                        </RequirePermission>
                      }
                    />
                    <Route path="" element={<ProfileTab />} />
                  </Route>
                </Route>
                <Route
                  path="/Communication"
                  element={
                    <RequirePermission
                      role={role}
                      isAdmin={isAdmin}
                      loading={roleLoading}
                      permission="chat-with-patient"
                    >
                      <MessagesPage />
                    </RequirePermission>
                  }
                >
                  <Route index element={<MessagesPage />} />
                  <Route path=":messageId" element={<MessagesPage />} />
                </Route>
                <Route
                  path="Task"
                  element={
                    <RequirePermission role={role} isAdmin={isAdmin} loading={roleLoading} permission="view-tasks">
                      <TasksPage />
                    </RequirePermission>
                  }
                >
                  <Route path=":taskId" element={null} />
                </Route>
                <Route
                  path="/billing"
                  element={
                    <RequirePermission role={role} isAdmin={isAdmin} loading={roleLoading} permission="manage-billing">
                      <BillingShell />
                    </RequirePermission>
                  }
                >
                  <Route index element={<Navigate to="/billing/claims" replace />} />
                  <Route path="claims" element={<ClaimsTab />} />
                  <Route path="eligibility" element={<EligibilityTab />} />
                  <Route path="patient-payments" element={<PatientPaymentsTab />} />
                  <Route path="chargemaster" element={<ChargemasterTab />} />
                </Route>
                <Route
                  path="/claims/:claimId"
                  element={
                    <RequirePermission role={role} isAdmin={isAdmin} loading={roleLoading} permission="manage-billing">
                      <ClaimDetailPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/triage"
                  element={
                    <RequirePermission role={role} isAdmin={isAdmin} loading={roleLoading} permission="review-triage">
                      <TriagePage />
                    </RequirePermission>
                  }
                />
                <Route path="OrderHSAT/:patientId" element={<OrderPatientHSATPage />} />
                <Route
                  path="RpmOrder/:patientId"
                  element={
                    <RequirePermission
                      role={role}
                      isAdmin={isAdmin}
                      loading={roleLoading}
                      permission="prescribe-cpap"
                      redirectTo="/billing/claims"
                    >
                      <RpmOrderPage />
                    </RequirePermission>
                  }
                />
                <Route path="DmeOrder/:patientId" element={<DmeOrderPage />} />
                <Route
                  path="/orders"
                  element={
                    <RequirePermission
                      role={role}
                      isAdmin={isAdmin}
                      loading={roleLoading}
                      permission="view-clinical-chart"
                      redirectTo="/billing/claims"
                    >
                      <OrdersPage />
                    </RequirePermission>
                  }
                />
                <Route path="/hsat" element={<Navigate to="/orders?view=hsat" replace />} />
                <Route path="/hsat-devices" element={<Navigate to="/orders?view=hsat" replace />} />
                <Route path="/notifications" element={<NotificationsPage />} />
                <Route
                  path="/admin/notification-templates"
                  element={
                    <RequirePermission
                      role={role}
                      isAdmin={isAdmin}
                      loading={roleLoading}
                      permission="manage-notification-templates"
                    >
                      <NotificationTemplateList />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/admin/notification-templates/by-key/:notificationKey"
                  element={
                    <RequirePermission
                      role={role}
                      isAdmin={isAdmin}
                      loading={roleLoading}
                      permission="manage-notification-templates"
                    >
                      <NotificationEditorByKey />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/admin/notification-templates/:templateId"
                  element={
                    <RequirePermission
                      role={role}
                      isAdmin={isAdmin}
                      loading={roleLoading}
                      permission="manage-notification-templates"
                    >
                      <NotificationTemplateEditor />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/admin/notification-preferences"
                  element={
                    <RequirePermission role={role} isAdmin={isAdmin} loading={roleLoading} permission="admin-panel">
                      <NotificationPreferences />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/invite-provider"
                  element={
                    <RequirePermission role={role} isAdmin={isAdmin} loading={roleLoading} permission="manage-staff">
                      <InviteProviderPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/admin/staffing"
                  element={
                    <RequirePermission role={role} isAdmin={isAdmin} loading={roleLoading} permission="view-staffing">
                      <StaffingPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/admin/staffing/:id"
                  element={
                    <RequirePermission role={role} isAdmin={isAdmin} loading={roleLoading} permission="view-staffing">
                      <StaffDetailPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/admin/zoom-test"
                  element={
                    <RequirePermission
                      role={role}
                      isAdmin={isAdmin}
                      loading={roleLoading}
                      permission="run-zoom-integration-test"
                    >
                      <ZoomIntegrationTest />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/admin/user-org-scope"
                  element={
                    <RequirePermission role={role} isAdmin={isAdmin} loading={roleLoading} permission="admin-panel">
                      <UserOrgScope />
                    </RequirePermission>
                  }
                />
                <Route path="/settings" element={<PractitionerSettingsPage />} />
                <Route path="/triage/composition/:compositionId" element={<CompositionReviewPage />} />
                <Route
                  path="/triage/composition/:compositionId/questionnaires"
                  element={<MissingQuestionnairesPage />}
                />
                <Route path="/onboarding/intake-form/:questionnaireResponseId" element={<IntakeReviewPage />} />
                <Route
                  path="/admin/provider-roles"
                  element={
                    <RequirePermission role={role} isAdmin={isAdmin} loading={roleLoading} permission="admin-panel">
                      <ProviderRolePage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/schedule"
                  element={
                    <RequirePermission
                      role={role}
                      isAdmin={isAdmin}
                      loading={roleLoading}
                      permission="schedule-appointment"
                    >
                      <SchedulePage />
                    </RequirePermission>
                  }
                />
                <Route path="/Appointment/:appointmentId/session" element={<AppointmentSessionPage />} />
                <Route path="/signin" element={<SignInPage />} />
                <Route path="/:resourceType" element={<SearchPage />} />
                <Route path="/:resourceType/new" element={<ResourceCreatePage />} />
                <Route path="/:resourceType/:id" element={<ResourcePage />}>
                  <Route path="" element={<ResourceDetailPage />} />
                  <Route path="edit" element={<ResourceEditPage />} />
                  <Route path="history" element={<ResourceHistoryPage />} />
                </Route>
              </>
            )}
            <Route path="/set-password" element={<SetPasswordPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/invite-expired" element={<InviteExpiredPage />} />
            {!profile && (
              <>
                <Route path="/" element={<LandingPage />} />
                <Route path="/signin" element={<SignInPage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </>
            )}
          </Routes>
        </RoutesWrapper>
      </Suspense>
    </AppShell>
  );
}
